import { IdentifiedServerMetric } from '@tableau/metrics-server-client-typescript/lib/src/p2/metricsServerApiTypes';
import { MetricTileProps } from '@tableau/metrics-shared-widgets-dom';
import * as _ from 'underscore';

import { IPlaceScope } from '../../app/IPlaceScope';
import { FeatureFlags } from '../FeatureFlags';
import { IEmptyTemplateModel } from '../models/IEmptyTemplate';
import { Module } from '../Module';
import { Metrics } from '../resources/Metrics';
import { BrowserTitleService } from '../services/BrowserTitleService';
import { ErrorState } from '../services/ErrorState';
import { ToasterService, ToastType } from '../services/ToasterService';
import { SubplaceWithDataGridScope } from './ContentSubplaceCtrl';

export interface IMetricsSubplaceScope extends ng.IScope, SubplaceWithDataGridScope<IdentifiedServerMetric>, IPlaceScope {
  metricsProps: MetricTileProps[];
}

export class MetricsCtrl {
  public static $inject = [
    '$scope',
    '$translate',
    'BrowserTitleService',
    'Metrics',
    'ErrorState',
    'ToasterService'
  ];

  private static emptyTemplateModel: IEmptyTemplateModel = { title: 'metricsEmptyMessage' };

  constructor(
    private $scope: IMetricsSubplaceScope,
    private $translate: ng.translate.ITranslateService,
    private BrowserTitleService: BrowserTitleService,
    metrics: Metrics,
    ErrorState: ErrorState,
    private toaster: ToasterService,
  ) {
    if (!FeatureFlags.metricsProxyEnabled) {
      ErrorState.go({
        detailsLocKey: 'pageDoesNotExist',
        redirectState: 'main.site.content.site.projects',
        redirectLocKey: 'backToContent'
      });
      return;
    }
    const metricsData: { [id: string]: string } = {};
    let returnedMetrics: ReadonlyArray<IdentifiedServerMetric>;
    this.BrowserTitleService.setTitleWithPlace({translatablePlaceName: 'metrics'});
    this.$scope.emptyTemplateModel = MetricsCtrl.emptyTemplateModel;
    this.$scope.metricsProps = [];

    const loadMetricsData = () => {
      returnedMetrics.forEach(metric => {
        metrics.getMetricData(metric.id)
          .then(data => {
            metricsData[metric.id] =
              _.find(data.columns, c => c.name === metric.metricColumnName).values[0].toString();
            showMetrics();
          })
          .catch(() => {
            this.toaster.pop({
              type: ToastType.Error,
              text: this.$translate.instant('metricsDataFetchError', { METRIC_NAME: metric.name }) });
            metricsData[metric.id] = this.$translate.instant('metricsErrorMessage');
            showMetrics();
          });
      });
    };

    const deleteMetric = (metric: IdentifiedServerMetric) => {
      const confirmMessage = this.$translate.instant('metricsConfirmDelete', { METRIC_NAME: metric.name });
      if (window.confirm(confirmMessage)) {
        metrics.deleteMetric(metric.id)
        .then(() => {
          returnedMetrics = returnedMetrics.filter(m => m.id !== metric.id);
          showMetrics();
        })
        .catch(() => {
          this.toaster.pop({
            type: ToastType.Error,
            text: this.$translate.instant('metricsDeleteError', { METRIC_NAME: metric.name }) });
          showMetrics();
        });
      }
    };

    const showMetrics = () => {
      $scope.$applyAsync(() => {
        $scope.metricsProps = returnedMetrics.map(metric => {
          return {
            title: metric.name,
            value: metricsData[metric.id] || this.$translate.instant('metricsLoading'),
            subtitle: metric.metricColumnName,
            testId: metric.id,
            width: '228px',
            style: { margin: '15px', float: 'left' } as React.CSSProperties,
            buttonLabel: this.$translate.instant('metricsDeleteMessage'),
            handleClick: () => { deleteMetric(metric); }
          };
        });
      });
    };

    metrics.getAllMetrics()
    .then(metrics => {
        returnedMetrics = metrics;
        showMetrics();
        loadMetricsData();
    })
    .catch(() => {
      this.toaster.pop({
        type: ToastType.Error,
        text: this.$translate.instant('metricsListFetchError') });
    });
  }
}

Module.instance().controller('MetricsCtrl', MetricsCtrl);
